import React, { useEffect, useState, useCallback } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime } from "../lib/timezone";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  MessageSquareText, Filter, Lock, ImageIcon, ExternalLink, Building2,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  bug: "Bug", feature: "Suggestion", confusing: "Confusing / hard to use",
  general: "General feedback", pilot_support: "Pilot support",
};
const STATUSES = ["new", "reviewing", "planned", "in_progress", "resolved", "closed", "not_planned"];
const STATUS_LABEL = {
  new: "New", reviewing: "Reviewing", planned: "Planned", in_progress: "In progress",
  resolved: "Resolved", closed: "Closed", not_planned: "Not planned",
};
const STATUS_CLS = {
  new: "bg-blue-100 text-blue-700", reviewing: "bg-indigo-100 text-indigo-700",
  planned: "bg-violet-100 text-violet-700", in_progress: "bg-amber-100 text-amber-700",
  resolved: "bg-emerald-100 text-emerald-700", closed: "bg-slate-200 text-slate-600",
  not_planned: "bg-rose-100 text-rose-700",
};
const SEV_CLS = {
  low: "bg-slate-100 text-slate-600", medium: "bg-sky-100 text-sky-700",
  high: "bg-orange-100 text-orange-700", blocking: "bg-red-100 text-red-700",
};
const ALL = "__all__";
const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

export default function PilotFeedback() {
  const { user } = useAuth();
  const tz = user?.org_timezone;
  const isSystemAdmin = user?.role === "system_admin";
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [filters, setFilters] = useState({ feedback_type: "", severity: "", status: "", division: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [resolution, setResolution] = useState("");
  const [duplicate, setDuplicate] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const { data } = await api.get(`/feedback?${params.toString()}`);
      setItems(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); }, []);

  const openDetail = async (f) => {
    setSelected(f);
    setDetail(null); setNote(""); setResolution(""); setDuplicate("");
    try {
      const { data } = await api.get(`/feedback/${f.id}`);
      setDetail(data);
      setResolution(data.resolution_note || "");
      setDuplicate(data.duplicate_of || "");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const patch = async (body, successMsg) => {
    try {
      const { data } = await api.patch(`/feedback/${selected.id}`, body);
      setDetail(data);
      if (body.add_internal_note) setNote("");
      toast.success(successMsg || "Updated");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const assignees = team.filter((u) => ["admin", "supervisor", "system_admin"].includes(u.role));
  const divisions = Array.from(new Set(items.map((i) => i.submitted_by_division).filter(Boolean)));
  const screenshotUrl = detail?.has_screenshot
    ? `${process.env.REACT_APP_BACKEND_URL}/api/feedback/${detail.id}/screenshot` : null;

  const FilterSelect = ({ field, placeholder, options }) => (
    <Select value={filters[field] || ALL} onValueChange={(v) => setFilters({ ...filters, [field]: v === ALL ? "" : v })}>
      <SelectTrigger className="h-9 w-40" data-testid={`filter-${field}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="animate-fade-up space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Administration</p>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Pilot Feedback</h1>
        <p className="mt-1 text-sm text-slate-500">
          {isSystemAdmin ? "Feedback across all pilot organizations." : "Feedback submitted by users in your organization."}
        </p>
      </div>

      <Card className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm" data-testid="feedback-filters">
        <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-400"><Filter className="h-3.5 w-3.5" /> Filters</div>
        <FilterSelect field="feedback_type" placeholder="All types" options={Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }))} />
        <FilterSelect field="severity" placeholder="All severity" options={["low", "medium", "high", "blocking"].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))} />
        <FilterSelect field="status" placeholder="All status" options={STATUSES.map((v) => ({ value: v, label: STATUS_LABEL[v] }))} />
        {divisions.length > 0 && <FilterSelect field="division" placeholder="All divisions" options={divisions.map((v) => ({ value: v, label: v }))} />}
        <div>
          <Label className="text-[10px] text-slate-400">From</Label>
          <Input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-9 w-36" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-400">To</Label>
          <Input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-9 w-36" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => setFilters({ feedback_type: "", severity: "", status: "", division: "", date_from: "", date_to: "" })}>Clear</Button>
      </Card>

      {items.length === 0 ? (
        <Card className="flex flex-col items-center rounded-md border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="feedback-empty">
          <MessageSquareText className="h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No feedback matches these filters.</p>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm" data-testid="feedback-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Sev</th>
                <th className="px-4 py-3">Title</th>
                {isSystemAdmin && <th className="px-4 py-3">Org</th>}
                <th className="px-4 py-3">By</th>
                <th className="px-4 py-3">Division</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((f) => (
                <tr key={f.id} data-testid={`feedback-row-${f.reference}`} onClick={() => openDetail(f)} className="cursor-pointer transition-colors hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono font-semibold text-slate-900">{f.reference}</td>
                  <td className="px-4 py-3 text-slate-600">{TYPE_LABEL[f.feedback_type]}</td>
                  <td className="px-4 py-3">{f.severity && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${SEV_CLS[f.severity]}`}>{f.severity}</span>}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{f.title}{f.allow_contact && <span className="ml-1 text-xs text-blue-500">· follow-up ok</span>}</td>
                  {isSystemAdmin && <td className="px-4 py-3 text-slate-500">{f.org_name}</td>}
                  <td className="px-4 py-3 text-slate-600">{f.submitted_by_name} <span className="text-xs text-slate-400">({f.submitted_by_role})</span></td>
                  <td className="px-4 py-3 text-slate-500">{f.submitted_by_division || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDateTime(f.created_at, tz)}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[f.status]}`}>{STATUS_LABEL[f.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="feedback-detail-dialog">
          {!detail ? <div className="h-40 animate-pulse rounded bg-slate-100" /> : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono">{detail.reference}</span>
                  <Badge variant="outline">{TYPE_LABEL[detail.feedback_type]}</Badge>
                  {detail.severity && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${SEV_CLS[detail.severity]}`}>{detail.severity}</span>}
                </DialogTitle>
                <DialogDescription>{detail.title}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="grid gap-3 rounded-md bg-slate-50 p-3 text-xs text-slate-600 sm:grid-cols-2">
                  {isSystemAdmin && <p className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {detail.org_name}</p>}
                  <p><span className="text-slate-400">By:</span> {detail.submitted_by_name} ({detail.submitted_by_role})</p>
                  <p><span className="text-slate-400">Division:</span> {detail.submitted_by_division || "—"}</p>
                  <p><span className="text-slate-400">Page:</span> {detail.page_feature || detail.route || "—"}</p>
                  <p><span className="text-slate-400">Submitted:</span> {fmtDateTime(detail.created_at, tz)}</p>
                  <p><span className="text-slate-400">Device:</span> {detail.device} · {detail.browser} · {detail.screen_size}</p>
                  <p><span className="text-slate-400">Build:</span> {detail.app_version}</p>
                  <p><span className="text-slate-400">Timezone:</span> {detail.org_timezone}</p>
                  <p><span className="text-slate-400">Contact:</span> {detail.allow_contact ? (detail.contact_email || detail.submitted_by_name) : "Follow-up not permitted"}</p>
                </div>

                <div>
                  <Label className={labelCls}>Description</Label>
                  <p className="mt-1 whitespace-pre-wrap text-slate-700">{detail.description}</p>
                </div>
                {detail.trying_to_do && <div><Label className={labelCls}>Trying to do</Label><p className="mt-1 text-slate-700">{detail.trying_to_do}</p></div>}
                {detail.what_happened && <div><Label className={labelCls}>What happened</Label><p className="mt-1 text-slate-700">{detail.what_happened}</p></div>}
                {detail.expected && <div><Label className={labelCls}>Expected</Label><p className="mt-1 text-slate-700">{detail.expected}</p></div>}

                {screenshotUrl && (
                  <div>
                    <Label className={labelCls}><ImageIcon className="mr-1 inline h-3 w-3" /> Screenshot</Label>
                    <a href={screenshotUrl} target="_blank" rel="noreferrer" data-testid="feedback-screenshot-link" className="mt-1 flex items-center gap-1 text-sm text-blue-600 hover:underline">
                      View screenshot <ExternalLink className="h-3 w-3" />
                    </a>
                    <img src={screenshotUrl} alt="screenshot" className="mt-2 max-h-64 rounded-md border border-slate-200" />
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className={labelCls}>Status</Label>
                    <Select value={detail.status} onValueChange={(v) => patch({ status: v }, "Status updated")}>
                      <SelectTrigger className="mt-1.5" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Assigned to</Label>
                    <Select value={detail.assigned_to || ALL} onValueChange={(v) => patch({ assigned_to: v === ALL ? "" : v }, "Assignment updated")}>
                      <SelectTrigger className="mt-1.5" data-testid="detail-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>Unassigned</SelectItem>
                        {assignees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className={labelCls}>Mark as duplicate of (reference)</Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input data-testid="detail-duplicate-input" value={duplicate} onChange={(e) => setDuplicate(e.target.value)} placeholder="e.g. SR-2026-0001" />
                    <Button variant="outline" onClick={() => patch({ duplicate_of: duplicate }, "Duplicate reference saved")}>Save</Button>
                  </div>
                </div>

                <div>
                  <Label className={labelCls}>Public resolution note (visible to submitter)</Label>
                  <Textarea data-testid="detail-resolution-input" value={resolution} onChange={(e) => setResolution(e.target.value)} rows={2} className="mt-1.5" />
                  <Button size="sm" className="mt-2" variant="outline" onClick={() => patch({ resolution_note: resolution }, "Resolution note saved")}>Save resolution note</Button>
                </div>

                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <Label className={`${labelCls} flex items-center gap-1`}><Lock className="h-3 w-3" /> Internal notes (admins only)</Label>
                  <div className="mt-2 space-y-2" data-testid="internal-notes-list">
                    {(detail.internal_notes || []).length === 0 && <p className="text-xs text-slate-400">No internal notes yet.</p>}
                    {(detail.internal_notes || []).map((n, i) => (
                      <div key={i} className="rounded border border-slate-200 bg-white p-2 text-sm">
                        <p className="text-slate-700">{n.text}</p>
                        <p className="mt-0.5 text-[11px] text-slate-400">{n.by_name} · {fmtDateTime(n.at, tz)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Textarea data-testid="internal-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an internal note…" />
                    <Button className="self-end bg-slate-800 hover:bg-slate-900 text-white" disabled={!note.trim()} onClick={() => patch({ add_internal_note: note }, "Note added")} data-testid="add-internal-note-button">Add</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
